import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import {
    CreateThemeRequest,
    ThemeAssetUploadResponse,
    ThemeListResponse,
    ThemeResponse,
    UpdateThemeRequest
} from "../models/theme.model";

/**
 * Largest `size` the paged list endpoints accept — the backend annotates it
 * `@Max(PaginationUtils.MAX_PAGE_SIZE)` and rejects anything above it with a 400.
 */
export const MAX_PAGE_SIZE = 200;

@Injectable({ providedIn: "root" })
export class ThemesApiService {
    private readonly http = inject(HttpClient);

    getTheme(themeId: string): Observable<ThemeResponse> {
        return this.http.get<ThemeResponse>(`${environment.interactiveExperienceServiceUrl}/v1/themes/${themeId}`);
    }

    getThemes(size: number, cursor?: string | null): Observable<ThemeListResponse> {
        let params = new HttpParams().set("size", size);
        if (cursor) {
            params = params.set("cursor", cursor);
        }
        return this.http.get<ThemeListResponse>(`${environment.interactiveExperienceServiceUrl}/v1/admin/themes`, {
            params
        });
    }

    /**
     * Wraps `getThemes` for callers that want every theme (e.g. a dropdown, or a lookup map),
     * not a paginated page. Not a true "getAll" — it's just `getThemes` at the largest size the backend accepts
     */
    getAllThemes(): Observable<ThemeListResponse> {
        return this.getThemes(MAX_PAGE_SIZE);
    }

    createTheme(body: CreateThemeRequest): Observable<ThemeResponse> {
        return this.http.post<ThemeResponse>(`${environment.interactiveExperienceServiceUrl}/v1/admin/themes`, body);
    }

    updateTheme(themeId: string, body: UpdateThemeRequest): Observable<ThemeResponse> {
        return this.http.put<ThemeResponse>(
            `${environment.interactiveExperienceServiceUrl}/v1/admin/themes/${themeId}`,
            body
        );
    }

    uploadThemeAsset(file: File): Observable<ThemeAssetUploadResponse> {
        const formData = new FormData();
        formData.append("file", file);
        // No explicit Content-Type: the browser sets multipart/form-data with the boundary.
        return this.http.post<ThemeAssetUploadResponse>(
            `${environment.interactiveExperienceServiceUrl}/v1/admin/themes/assets`,
            formData
        );
    }
}
